import React, { useEffect, useRef } from "react";
import { Button } from "antd";
import { Routes, Route, useLocation, useNavigate } from "react-router-dom";
import { LegalFooter, ImpressumPage, PrivacyPage } from "@dowty/legal";
import { useTranslation } from "react-i18next";
import Beerpong from "./components/Trinkspiele/beerpong.component";
import TournamentManagerPage from "./components/Trinkspiele/beerpong/TournamentManagerPage";
import Drinkhelper from "./components/drinkhelper";
import Sidebar from "./components/Sidebar";
import SaufAlone from "./components/saufhelper/drinkhelperOne";
import SaufTwo from "./components/saufhelper/drinkhelperTwo";
import SaufWithYourFriends from "./components/saufhelper/drinkhelperMore";
import Busfahren from "./components/Trinkspiele/busfahren.component";
import KingCup from "./components/Trinkspiele/kingCup.component";
import ShittyGame from "./components/Trinkspiele/shitty.component";
import RageCage from "./components/Trinkspiele/rageCage";
import Hawaii from "./components/Trinkspiele/hawaii";
import DontDrinkAndDrive from "./components/Trinkspiele/dontDrinkAndDrive";
import HigherOrLower from "./components/Trinkspiele/higherOrLower";
import Holzfaellern from "./components/Trinkspiele/holzfaellern";
import Laughfool from "./components/Trinkspiele/laughtfools";
import Lieing from "./components/Trinkspiele/lieing";
import Music from "./components/music.component";
import Quizzler from "./components/Trinkspiele/quizzduell";
import LieingCards from "./components/Trinkspiele/lieing_Cards";
import LieingCubes from "./components/Trinkspiele/lieing_Cubes";
import FlunkyBall from "./components/Trinkspiele/flunky";
import HorseRaising from "./components/Trinkspiele/horseraising";
import ItiGame from "./components/Trinkspiele/iti";
import ItiGameSequential from "./components/Trinkspiele/itiSckMe";
import Profile from "./components/drinks/Profile";
import AddDrink from "./components/drinks/AddDrink";
import DrinkStats from "./components/drinks/DrinkStats";
import DrinkHistory from "./components/drinks/DrinkHistory";
import YearInReview from "./components/drinks/YearInReview";
import EventStats from "./components/drinks/EventStats";
import GrichischerTempel from "./components/Trinkspiele/GrichischerTempel";
import { applyUpdate, registerServiceWorker, useOutboxSync } from "@dowty/offline";
import { outbox } from "./offline";
import { useAuth } from "./context/AuthContext";
import { useAppFeedback } from "./components/useAppFeedback";
import EventTournamentsPage from "./tournament/EventTournamentsPage";
import { TournamentWizardPage, OrganizerTournamentPage, PlayerTournamentPage } from "./tournament/TournamentPage";

const App: React.FC = () => {
    // Flushes the offline drink outbox on start, on reconnect and when the tab becomes visible.
    useOutboxSync(outbox);
    useEffect(() => {
        void navigator.storage?.persist?.().catch(() => undefined);
    }, []);
    const { t, i18n } = useTranslation();
    const { pathname, hash } = useLocation();
    const navigate = useNavigate();
    const { isAuthenticated } = useAuth();
    const { notification } = useAppFeedback();
    const handledStandaloneLaunch = useRef(false);
    const language = (i18n.resolvedLanguage ?? i18n.language ?? "en").toLowerCase().startsWith("de") ? "de" : "en";

    useEffect(() => {
        if (!hash) window.scrollTo(0, 0);
    }, [pathname, hash]);

    useEffect(() => {
        if (process.env.NODE_ENV === "production") {
            registerServiceWorker(`${process.env.PUBLIC_URL}/service-worker.js`, {
                onUpdate: (registration) => {
                    // A new build finished installing while an older one is still controlling this tab (e.g.
                    // this device already had the app open when a new version deployed). Ask before switching -
                    // force-reloading someone mid drink-log would be worse than a slightly stale app shell for a
                    // few more minutes. If they dismiss it, the same prompt reappears on the next deploy (or the
                    // next full reload picks up the new worker on its own, since the browser re-checks for
                    // updates on navigation) - so nobody is stuck on a stale build forever, they're just never
                    // forced off the current one mid-task.
                    notification.info({
                        message: t("app.updateAvailable"),
                        btn: <Button onClick={() => applyUpdate(registration)}>{t("app.reload")}</Button>,
                        duration: 0,
                    });
                },
            });
        }
    }, [notification, t]);

    useEffect(() => {
        if (handledStandaloneLaunch.current || !isAuthenticated) return;
        handledStandaloneLaunch.current = true;
        if (pathname === "/" && window.matchMedia?.("(display-mode: standalone)").matches) {
            navigate("/drinks/add", { replace: true });
        }
    }, [isAuthenticated, navigate, pathname]);
    const content = (
        <div className="sf-page-content">
            <Routes>
                <Route path="/impressum" element={<ImpressumPage app="saufdings" language={language} />} />
                <Route path="/datenschutz" element={<PrivacyPage app="saufdings" language={language} />} />
                <Route path="/" element={<Drinkhelper />} />
                <Route path="/tournaments" element={<EventTournamentsPage />} />
                <Route path="/events/:eventId/tournaments" element={<EventTournamentsPage />} />
                <Route path="/tournaments/new" element={<TournamentWizardPage />} />
                <Route path="/tournaments/:id/player" element={<PlayerTournamentPage />} />
                <Route path="/tournaments/:id" element={<OrganizerTournamentPage />} />
                <Route path="*" element={<Drinkhelper />} />
                <Route path="/beerpong" element={<Beerpong />} />
                <Route path="/beerpong/tournaments" element={<TournamentManagerPage />} />
                <Route path="/saufAlone" element={<SaufAlone />} />
                <Route path="/saufTwo" element={<SaufTwo />} />
                <Route path="/saufwithyourfriends" element={<SaufWithYourFriends />} />
                <Route path="/rideTheBus" element={<Busfahren />} />
                <Route path="/kingCup" element={<KingCup />} />
                <Route path="/greekTempel" element={<GrichischerTempel />} />
                <Route path="/shitty" element={<ShittyGame />} />
                <Route path="/ragecage" element={<RageCage />} />
                <Route path="/hawaii" element={<Hawaii />} />
                <Route path="/dontDrinkAndDrive" element={<DontDrinkAndDrive />} />
                <Route path="/higherOrLower" element={<HigherOrLower />} />
                <Route path="/holzfaellern" element={<Holzfaellern />} />
                <Route path="/laughfool" element={<Laughfool />} />
                <Route path="/lieing" element={<Lieing />} />
                <Route path="/music" element={<Music />} />
                <Route path="/quizzler" element={<Quizzler />} />
                <Route path="/lieing/lieingCards" element={<LieingCards />} />
                <Route path="/lieing/lieingCubes" element={<LieingCubes />} />
                <Route path="/flunky" element={<FlunkyBall />} />
                <Route path="/horseRaising" element={<HorseRaising />} />
                <Route path="/iti" element={<ItiGame />} />
                <Route path="/itiEatMyAss" element={<ItiGameSequential />} />
                <Route path="/profile" element={<Profile />} />
                <Route path="/drinks/add" element={<AddDrink />} />
                <Route path="/drinks/stats" element={<DrinkStats />} />
                <Route path="/drinks/recap" element={<YearInReview />} />
                <Route path="/drinks/history" element={<DrinkHistory />} />
                <Route path="/events/stats" element={<EventStats />} />
            </Routes>
            {(pathname === "/impressum" || pathname === "/datenschutz") && <LegalFooter
                language={language}
                links={{ impressum: "/impressum", privacy: "/datenschutz" }}
            />}
        </div>
    );
    return pathname === "/impressum" || pathname === "/datenschutz" ? content : <Sidebar>{content}</Sidebar>;
};

export default App;
